import React, { useState } from 'react';
import { useAtom } from 'jotai';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, Paper, styled, tableCellClasses } from '@mui/material';
import { allDeviceAtom, Device } from '../atoms-types/devices';
import { useNavigate } from 'react-router-dom';

type Order = 'asc' | 'desc';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: 'dimgrey',
    color: theme.palette.common.white,
    '&:hover': {
      color: 'goldenrod',
    },
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const CustomTableSortLabel = styled(TableSortLabel)(({ theme, active }) => ({
  color: 'inherit',
  '&:hover': {
    color: 'orange', 
    '& .MuiTableSortLabel-icon': {
      color: 'orange',
    },
  },
  ...(active && {
    color: 'lightblue !important',
    '& .MuiTableSortLabel-icon': {
      color: 'lightblue !important', 
    },
  }),
}));

const DeviceTable: React.FC = () => {
  const [allDevices] = useAtom(allDeviceAtom);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Device>('deviceEUI');
  const navigate = useNavigate();

  const handleRequestSort = (property: keyof Device) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortComparator = (a: Device, b: Device, orderBy: keyof Device) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const sortedDevices = [...allDevices].sort((a, b) => {
    return order === 'asc'
      ? sortComparator(a, b, orderBy)
      : -sortComparator(a, b, orderBy);
  });

  const handleDeviceClick = (deviceEUI: string) => {
    navigate(`/fleet/${deviceEUI}`);
  };

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <StyledTableRow>
            {[
              'deviceEUI',
              'name',
              'type',
              'SOC', 
              'TbattLvl', 
              'lastRx', 
              'RSSI', 
              'lastIncidentCode',
              'carUID'
            ].map((column) => (
              <StyledTableCell key={column}>
                <CustomTableSortLabel
                  active={orderBy === column}
                  direction={orderBy === column ? order : 'asc'}
                  onClick={() => handleRequestSort(column as keyof Device)}
                >
                  {column}
                </CustomTableSortLabel>
              </StyledTableCell>
            ))}
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {sortedDevices.map((device) => (
            <TableRow key={device.deviceEUI}>
              <TableCell
                onClick={() => handleDeviceClick(device.name)}
                style={{ cursor: 'pointer', color: 'grey', textDecoration: 'underline' }}
              >
                {device.deviceEUI}
              </TableCell>
              <TableCell>{device.name}</TableCell>
              <TableCell>{device.type}</TableCell>
              <TableCell>{device.SOC}</TableCell>
              <TableCell>{device.TbattLvl}</TableCell>
              <TableCell>{device.lastRx}</TableCell>
              <TableCell>{device.RSSI}</TableCell>
              <TableCell>{device.lastIncidentCode}</TableCell>
              <TableCell>{device.carUID}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DeviceTable;



